/**
 * Solana WebSocket Manager
 * 
 * This service manages WebSocket connections to the Solana Tracker API.
 * It handles room subscriptions, reconnection, and event dispatching.
 */

import { env } from '../config/env';
import EventEmitter from 'eventemitter3';
import { Token } from '../types/supabase';

class SolanaWebSocketManager {
  private static instance: SolanaWebSocketManager;
  
  private socket: WebSocket | null = null;
  private isConnected: boolean = false;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  
  // Room subscriptions
  private subscribedRooms: Set<string> = new Set();
  
  // Event emitter for handling events
  private emitter: EventEmitter = new EventEmitter();
  
  // Base WebSocket URL
  private wsUrl: string = env.SOLANA_TRACKER_WS_URL;
  
  private constructor() {
    // Private constructor to enforce singleton pattern
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): SolanaWebSocketManager {
    if (!SolanaWebSocketManager.instance) {
      SolanaWebSocketManager.instance = new SolanaWebSocketManager();
    }
    return SolanaWebSocketManager.instance;
  }
  
  /**
   * Initialize the WebSocket connection
   */
  public async initialize(): Promise<void> {
    if (this.isConnected || this.isConnecting) return;
    
    try {
      await this.connect();
    } catch (error) {
      console.error('Failed to initialize WebSocket connection:', error);
      throw error;
    }
  }
  
  /**
   * Connect to the WebSocket server
   */
  private async connect(): Promise<void> {
    if (this.isConnected || this.isConnecting) return;
    
    this.isConnecting = true;
    
    try {
      // Create a new WebSocket connection
      this.socket = new WebSocket(this.wsUrl);
      
      // Set up event listeners
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      
      // Wait for connection to be established
      await new Promise<void>((resolve, reject) => {
        const onOpen = () => {
          this.socket?.removeEventListener('open', onOpen);
          this.socket?.removeEventListener('error', onError);
          resolve();
        };
        
        const onError = (error: Event) => {
          this.socket?.removeEventListener('open', onOpen);
          this.socket?.removeEventListener('error', onError);
          reject(error);
        };
        
        this.socket?.addEventListener('open', onOpen);
        this.socket?.addEventListener('error', onError);
        
        // Add timeout
        setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);
      });
      
    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }
  
  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    console.log('WebSocket connection established');
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Start heartbeat to keep connection alive
    this.startHeartbeat();
    
    // Emit connected event
    this.emitter.emit('connected');
    
    // Rejoin previously subscribed rooms
    this.rejoinRooms();
  }
  
  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
    this.isConnected = false;
    this.isConnecting = false;
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Attempt to reconnect
    this.scheduleReconnect();
  }
  
  /**
   * Handle WebSocket error event
   */
  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    this.emitter.emit('error', error);
    
    // Connection will be closed automatically after an error
  }
  
  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      
      // Check if it's a system message
      if (data.type === 'system') {
        this.handleSystemMessage(data);
        return;
      }
      
      // Check if it's a room message
      if (data.room) {
        // Emit event for the specific room
        this.emitter.emit(data.room, data.data);
        
        // Also emit a generic event for the message type
        if (data.type) {
          this.emitter.emit(data.type, data.data);
        }
      }
      
      // Emit a generic message event
      this.emitter.emit('message', data);
      
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }
  
  /**
   * Handle system messages
   */
  private handleSystemMessage(message: any): void {
    // Handle system messages like pings, authentication, etc.
    if (message.subtype === 'ping') {
      this.sendPong();
    }
  }
  
  /**
   * Send a pong response to keep the connection alive
   */
  private sendPong(): void {
    if (!this.isConnected || !this.socket) return;
    
    try {
      this.socket.send(JSON.stringify({ type: 'system', subtype: 'pong' }));
    } catch (error) {
      console.error('Failed to send pong:', error);
    }
  }
  
  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.socket) {
        try {
          this.socket.send(JSON.stringify({ type: 'system', subtype: 'heartbeat' }));
        } catch (error) {
          console.error('Failed to send heartbeat:', error);
        }
      }
    }, 30000); // Send heartbeat every 30 seconds
  }
  
  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
  
  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Maximum reconnection attempts reached');
      return;
    }
    
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    
    console.log(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimeout = setTimeout(() => {
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.connect().catch(error => {
        console.error('Reconnection attempt failed:', error);
      });
    }, delay);
  }
  
  /**
   * Ensure WebSocket connection is established
   */
  public async ensureConnection(): Promise<void> {
    if (!this.isConnected && !this.isConnecting) {
      await this.connect();
    }
  }
  
  /**
   * Join a room to receive events from
   */
  public joinRoom(room: string): void {
    if (!room) return;
    
    // Add to subscribed rooms
    this.subscribedRooms.add(room);
    
    // Send join message if connected
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify({ type: 'join', room }));
        console.log(`Joined room: ${room}`);
      } catch (error) {
        console.error(`Failed to join room ${room}:`, error);
      }
    }
  }
  
  /**
   * Leave a room to stop receiving events
   */
  public leaveRoom(room: string): void {
    if (!room) return;
    
    // Remove from subscribed rooms
    this.subscribedRooms.delete(room);
    
    // Send leave message if connected
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify({ type: 'leave', room }));
        console.log(`Left room: ${room}`);
      } catch (error) {
        console.error(`Failed to leave room ${room}:`, error);
      }
    }
  }
  
  /**
   * Rejoin all previously subscribed rooms after reconnection
   */
  private rejoinRooms(): void {
    if (!this.isConnected || !this.socket) return;
    
    this.subscribedRooms.forEach(room => {
      try {
        this.socket?.send(JSON.stringify({ type: 'join', room }));
        console.log(`Rejoined room: ${room}`);
      } catch (error) {
        console.error(`Failed to rejoin room ${room}:`, error);
      }
    });
  }
  
  /**
   * Subscribe to an event
   */
  public on(event: string, listener: (data: any) => void): () => void {
    this.emitter.on(event, listener);
    
    // Return an unsubscribe function
    return () => {
      this.emitter.off(event, listener);
    };
  }
  
  /**
   * Clean up resources
   */
  public cleanup(): void {
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Cancel reconnection
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Close WebSocket connection
    if (this.socket) {
      try {
        this.socket.close();
      } catch (error) {
        console.error('Failed to close WebSocket connection:', error);
      }
      this.socket = null;
    }
    
    // Reset state
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Clear event listeners
    this.emitter.removeAllListeners();
    
    // Clear subscribed rooms
    this.subscribedRooms.clear();
  }
}

// Export singleton instance
const solanaWebSocketManager = SolanaWebSocketManager.getInstance();
export default solanaWebSocketManager; 